import React from 'react';
import { Achievement, AchievementDefinition, AchievementTier } from '../types/mundus';

interface AchievementCardProps {
  achievement: Achievement | AchievementDefinition;
  isUnlocked?: boolean;
  size?: 'small' | 'medium' | 'large';
}

export const AchievementCard: React.FC<AchievementCardProps> = ({
  achievement,
  isUnlocked = true,
  size = 'medium',
}) => {
  const getTierColor = (tier: AchievementTier): {border: string; glow: string; gradient: string; text: string} => {
    switch (tier) {
      case 'bronze':
        return {
          border: 'border-amber-600',
          glow: 'shadow-lg shadow-amber-600/50',
          gradient: 'from-amber-600 to-amber-800',
          text: 'text-amber-500',
        };
      case 'silver':
        return {
          border: 'border-gray-400',
          glow: 'shadow-lg shadow-gray-400/50',
          gradient: 'from-gray-400 to-gray-600',
          text: 'text-gray-400',
        };
      case 'gold':
        return {
          border: 'border-yellow-400',
          glow: 'shadow-lg shadow-yellow-400/50',
          gradient: 'from-yellow-400 to-yellow-600',
          text: 'text-yellow-400',
        };
      case 'platinum':
        return {
          border: 'border-cyber-neon-purple',
          glow: 'shadow-lg shadow-purple-500/50',
          gradient: 'from-purple-400 to-purple-600',
          text: 'text-cyber-neon-purple',
        };
      default:
        return {
          border: 'border-gray-500',
          glow: 'shadow-lg',
          gradient: 'from-gray-400 to-gray-600',
          text: 'text-gray-400',
        };
    }
  };

  const getCategoryIcon = (category: string): string => {
    switch (category) {
      case 'milestone':
        return '🏁';
      case 'streak':
        return '🔥';
      case 'collection':
        return '📦';
      case 'mastery':
        return '⭐';
      case 'progress':
        return '📈';
      case 'special':
        return '✨';
      default:
        return '🏆';
    }
  };

  const sizeClasses = {
    small: 'p-3',
    medium: 'p-4',
    large: 'p-6',
  };

  const iconSizeClasses = {
    small: 'text-3xl',
    medium: 'text-4xl',
    large: 'text-5xl',
  };

  const tierColors = getTierColor(achievement.tier);
  const unlockedAt = 'unlockedAt' in achievement ? achievement.unlockedAt : null;

  return (
    <div
      className={`
        cyber-card relative border-2 overflow-hidden transition-all duration-300
        ${isUnlocked ? `${tierColors.border} ${tierColors.glow} hover:scale-105` : 'border-cyber-border opacity-40 grayscale'}
        ${sizeClasses[size]}
      `}
    >
      {/* Tier badge */}
      <div
        className={`
          absolute top-2 right-2 px-3 py-1 rounded-full text-xs font-display font-bold text-white uppercase tracking-wider
          bg-gradient-to-br ${tierColors.gradient}
        `}
      >
        {achievement.tier}
      </div>

      {/* Category badge */}
      <div className="absolute top-2 left-2 text-xl">{getCategoryIcon(achievement.category)}</div>

      {!isUnlocked && (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-6xl opacity-20">
          🔒
        </div>
      )}

      {/* Achievement content */}
      <div className="flex items-start space-x-3 mt-6">
        {/* Icon */}
        <div className={`flex-shrink-0 ${iconSizeClasses[size]} ${isUnlocked ? 'animate-float' : ''}`}>
          {achievement.iconUrl}
        </div>

        {/* Details */}
        <div className="flex-1 min-w-0">
          <h3 className={`font-display font-bold uppercase tracking-wide ${
            isUnlocked ? 'text-cyber-text-primary' : 'text-cyber-text-muted'
          }`}>
            {achievement.title}
          </h3>
          <p className={`text-xs mt-1 font-mono ${
            isUnlocked ? 'text-cyber-text-secondary' : 'text-cyber-text-muted'
          }`}>
            {achievement.description}
          </p>

          {/* Points */}
          <div className="flex items-center justify-between mt-3">
            <span className={`text-sm font-display font-bold ${
              isUnlocked ? tierColors.text : 'text-cyber-text-muted'
            }`}>
              {achievement.points} PTS
            </span>

            {/* Unlock date */}
            {isUnlocked && unlockedAt && (
              <span className="text-xs text-cyber-text-muted font-mono">
                {new Date(unlockedAt).toLocaleDateString()}
              </span>
            )}

            {!isUnlocked && (
              <span className="text-xs text-cyber-text-muted font-mono italic">LOCKED</span>
            )}
          </div>
        </div>
      </div>

      {/* Bottom glow effect */}
      {isUnlocked && (
        <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${tierColors.gradient} opacity-50`}></div>
      )}
    </div>
  );
};

export default AchievementCard;
